/**
 * Copyright (c) 2024-2025 FFOLLOWME OÜ (Reg. No. 16933606)
 * All rights reserved.
 * 
 * This file is part of the Nardium Platform ecosystem.
 * Unauthorized copying, modification, or distribution is prohibited.
 */

import React, { useState } from 'react';
import { Bluetooth, Loader2, AlertCircle, CheckCircle2, Settings, Wifi, Wallet } from 'lucide-react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { cn } from '../ui/utils';
import { toast } from 'sonner';

type DeviceStatus = 'disconnected' | 'connecting' | 'connected' | 'error';

interface NariStats {
  uptime: string;
  alerts: number;
  battery: number;
}

export function NariDeviceConnector() {
  const [deviceStatus, setDeviceStatus] = useState<DeviceStatus>('disconnected');
  const [stats, setStats] = useState<NariStats>({
    uptime: '0h 0m',
    alerts: 0,
    battery: 100
  });

  // TODO: Implementar conexión Web Bluetooth + firma de wallet aquí
  // Evento: onClick → iniciar navigator.bluetooth.requestDevice({
  //   filters: [{ name: 'Nari' }],
  //   optionalServices: ['battery_service']
  // })
  const handleConnectDevice = async () => {
    setDeviceStatus('connecting');
    toast.info('Searching for Nari device...');

    // Simulate Bluetooth pairing process
    setTimeout(() => {
      const success = Math.random() > 0.2; // 80% success rate for demo
      
      if (success) {
        setDeviceStatus('connected');
        setStats({
          uptime: '24h 15m',
          alerts: 142,
          battery: 87
        });
        toast.success('Nari connected successfully! 🐉');
      } else {
        setDeviceStatus('error');
        toast.error('Failed to connect to Nari device');
      }
    }, 3000);
  };

  const handleCancelConnection = () => {
    setDeviceStatus('disconnected');
    toast.info('Connection cancelled');
  };

  const handleRetry = () => {
    setDeviceStatus('disconnected');
    setTimeout(() => handleConnectDevice(), 100);
  };

  const handleDisconnect = () => {
    setDeviceStatus('disconnected');
    setStats({
      uptime: '0h 0m',
      alerts: 0,
      battery: 100
    });
    toast.info('Nari disconnected');
  };

  const renderDragonAvatar = () => {
    const baseClasses = "w-48 h-48 mx-auto transition-all duration-500";
    
    switch (deviceStatus) {
      case 'disconnected':
        return (
          <div className={cn(baseClasses, "opacity-50 grayscale")}>
            <div className="relative">
              <svg viewBox="0 0 200 200" className="w-full h-full">
                {/* Dragon silhouette - disconnected (gray, eyes off) */}
                <path
                  d="M100 30 L120 50 L140 45 L150 60 L165 65 L170 85 L165 100 L155 115 L140 125 L120 135 L100 140 L80 135 L60 125 L45 115 L35 100 L30 85 L35 65 L50 60 L60 45 L80 50 Z"
                  fill="#4B5563"
                  stroke="#374151"
                  strokeWidth="2"
                />
                {/* Eyes off */}
                <circle cx="80" cy="80" r="6" fill="#1F2937" />
                <circle cx="120" cy="80" r="6" fill="#1F2937" />
                {/* Wings */}
                <path d="M50 90 L30 100 L20 120 L30 115 L45 105 Z" fill="#4B5563" opacity="0.6" />
                <path d="M150 90 L170 100 L180 120 L170 115 L155 105 Z" fill="#4B5563" opacity="0.6" />
              </svg>
            </div>
          </div>
        );
      
      case 'connecting':
        return (
          <div className={cn(baseClasses, "animate-pulse")}>
            <div className="relative">
              <svg viewBox="0 0 200 200" className="w-full h-full">
                {/* Dragon silhouette - connecting (blue, eyes blinking) */}
                <path
                  d="M100 30 L120 50 L140 45 L150 60 L165 65 L170 85 L165 100 L155 115 L140 125 L120 135 L100 140 L80 135 L60 125 L45 115 L35 100 L30 85 L35 65 L50 60 L60 45 L80 50 Z"
                  fill="#3B82F6"
                  stroke="#2563EB"
                  strokeWidth="2"
                  className="animate-pulse"
                />
                {/* Eyes blinking */}
                <circle cx="80" cy="80" r="6" fill="#60A5FA" className="animate-ping" />
                <circle cx="120" cy="80" r="6" fill="#60A5FA" className="animate-ping" />
                {/* Wings */}
                <path d="M50 90 L30 100 L20 120 L30 115 L45 105 Z" fill="#3B82F6" opacity="0.7" />
                <path d="M150 90 L170 100 L180 120 L170 115 L155 105 Z" fill="#3B82F6" opacity="0.7" />
              </svg>
            </div>
          </div>
        );
      
      case 'connected':
        return (
          <div className={cn(baseClasses, "drop-shadow-2xl")}>
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-yellow-500/20 to-orange-500/20 rounded-full blur-xl animate-pulse" />
              <svg viewBox="0 0 200 200" className="w-full h-full relative z-10">
                {/* Dragon silhouette - connected (golden, green eyes) */}
                <path
                  d="M100 30 L120 50 L140 45 L150 60 L165 65 L170 85 L165 100 L155 115 L140 125 L120 135 L100 140 L80 135 L60 125 L45 115 L35 100 L30 85 L35 65 L50 60 L60 45 L80 50 Z"
                  fill="url(#goldGradient)"
                  stroke="#F59E0B"
                  strokeWidth="2"
                />
                {/* Eyes glowing green */}
                <circle cx="80" cy="80" r="7" fill="#10B981" />
                <circle cx="80" cy="80" r="3" fill="#ECFDF5" className="animate-pulse" />
                <circle cx="120" cy="80" r="7" fill="#10B981" />
                <circle cx="120" cy="80" r="3" fill="#ECFDF5" className="animate-pulse" />
                {/* Wings extended */}
                <path d="M50 90 L30 100 L20 120 L30 115 L45 105 Z" fill="url(#goldGradient)" opacity="0.9" />
                <path d="M150 90 L170 100 L180 120 L170 115 L155 105 Z" fill="url(#goldGradient)" opacity="0.9" />
                {/* Fire breath effect */}
                <path d="M100 100 L105 110 L100 120 L95 110 Z" fill="#F59E0B" opacity="0.7" className="animate-pulse" />
                
                <defs>
                  <linearGradient id="goldGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#FFD700" />
                    <stop offset="50%" stopColor="#FFA500" />
                    <stop offset="100%" stopColor="#FF8C00" />
                  </linearGradient>
                </defs>
              </svg>
            </div>
          </div>
        );
      
      case 'error':
        return (
          <div className={cn(baseClasses, "opacity-80")}>
            <div className="relative">
              <svg viewBox="0 0 200 200" className="w-full h-full">
                {/* Dragon silhouette - error (red, wings closed) */}
                <path
                  d="M100 30 L120 50 L140 45 L150 60 L165 65 L170 85 L165 100 L155 115 L140 125 L120 135 L100 140 L80 135 L60 125 L45 115 L35 100 L30 85 L35 65 L50 60 L60 45 L80 50 Z"
                  fill="#DC2626"
                  stroke="#991B1B"
                  strokeWidth="2"
                />
                {/* Sad eyes */}
                <path d="M75 85 L85 80" stroke="#7F1D1D" strokeWidth="3" strokeLinecap="round" />
                <path d="M115 80 L125 85" stroke="#7F1D1D" strokeWidth="3" strokeLinecap="round" />
                {/* Wings closed */}
                <path d="M50 95 L40 100 L45 105 Z" fill="#DC2626" opacity="0.5" />
                <path d="M150 95 L160 100 L155 105 Z" fill="#DC2626" opacity="0.5" />
              </svg>
            </div>
          </div>
        );
    }
  };

  const renderContent = () => {
    switch (deviceStatus) {
      case 'disconnected':
        return (
          <>
            {renderDragonAvatar()}
            <div className="text-center mt-8 space-y-4">
              <h2 className="text-3xl font-bold bg-gradient-to-r from-slate-200 to-slate-400 bg-clip-text text-transparent">
                Add Nari
              </h2>
              <p className="text-slate-400 text-lg max-w-md mx-auto">
                Connect your guardian dragon to receive real-time market alerts.
              </p>
              <Button
                onClick={handleConnectDevice}
                data-action="connect-nari"
                className="mt-6 bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white px-8 py-6 text-lg rounded-xl shadow-lg hover:shadow-xl transition-all"
              >
                <Bluetooth className="mr-2 h-5 w-5" />
                Search Device
              </Button>
            </div>
          </>
        );
      
      case 'connecting':
        return (
          <>
            {renderDragonAvatar()}
            <div className="text-center mt-8 space-y-4">
              <h2 className="text-3xl font-bold text-blue-400 flex items-center justify-center gap-3">
                <Loader2 className="animate-spin h-8 w-8" />
                Searching for Nari...
              </h2>
              <p className="text-slate-400 text-lg max-w-md mx-auto">
                Activate pairing mode on your dragon (blue blinking light).
              </p>
              <Button
                onClick={handleCancelConnection}
                variant="outline"
                className="mt-6 border-slate-600 text-slate-300 hover:bg-slate-800"
              >
                Cancel
              </Button>
            </div>
          </>
        );
      
      case 'connected':
        return (
          <>
            {renderDragonAvatar()}
            <div className="text-center mt-8 space-y-4">
              <h2 className="text-3xl font-bold bg-gradient-to-r from-yellow-400 to-orange-500 bg-clip-text text-transparent">
                Nari is with you! 🔥
              </h2>
              <p className="text-slate-300 text-lg max-w-md mx-auto">
                Protecting your portfolio with dragon wisdom
              </p>
              
              <div className="flex items-center justify-center gap-2 mt-4 flex-wrap">
                <Badge className="bg-green-500/20 text-green-400 border-green-500/30 px-4 py-2">
                  <CheckCircle2 className="mr-2 h-4 w-4" />
                  Connected
                </Badge>
                <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30 px-4 py-2">
                  <Wifi className="mr-2 h-4 w-4" />
                  Wi-Fi
                </Badge>
                <Badge className="bg-purple-500/20 text-purple-400 border-purple-500/30 px-4 py-2">
                  <Wallet className="mr-2 h-4 w-4" />
                  Wallet Synced
                </Badge>
              </div>

              {/* Stats Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-8">
                <Card className="bg-slate-800/50 border-slate-700 p-4">
                  <div className="text-slate-400 text-sm mb-1">Uptime</div>
                  <div className="text-2xl font-bold text-white">{stats.uptime}</div>
                </Card>
                <Card className="bg-slate-800/50 border-slate-700 p-4">
                  <div className="text-slate-400 text-sm mb-1">Alerts Sent</div>
                  <div className="text-2xl font-bold text-orange-400">{stats.alerts}</div>
                </Card>
                <Card className="bg-slate-800/50 border-slate-700 p-4">
                  <div className="text-slate-400 text-sm mb-1">Battery</div>
                  <div className="text-2xl font-bold text-green-400">{stats.battery}%</div>
                </Card>
              </div>

              <div className="flex gap-4 justify-center mt-6">
                <Button
                  onClick={() => toast.info('Settings panel coming soon!')}
                  className="bg-slate-700 hover:bg-slate-600 text-white"
                >
                  <Settings className="mr-2 h-4 w-4" />
                  View Settings
                </Button>
                <Button
                  onClick={handleDisconnect}
                  variant="outline"
                  className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                >
                  Disconnect
                </Button>
              </div>
            </div>
          </>
        );
      
      case 'error':
        return (
          <>
            {renderDragonAvatar()}
            <div className="text-center mt-8 space-y-4">
              <h2 className="text-3xl font-bold text-red-400 flex items-center justify-center gap-3">
                <AlertCircle className="h-8 w-8" />
                Nari Not Found
              </h2>
              <p className="text-slate-400 text-lg max-w-md mx-auto">
                Is it turned on and in pairing mode?
              </p>
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 max-w-md mx-auto text-left">
                <h3 className="text-red-400 font-semibold mb-2">Troubleshooting:</h3>
                <ul className="text-slate-400 text-sm space-y-1 list-disc list-inside">
                  <li>Check if Nari is powered on</li>
                  <li>Enable Bluetooth on your device</li>
                  <li>Hold the pairing button for 3 seconds</li>
                  <li>Move closer to the Nari device</li>
                </ul>
              </div>
              <Button
                onClick={handleRetry}
                data-action="connect-nari"
                className="mt-6 bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white px-8 py-6 text-lg rounded-xl"
              >
                Retry Connection
              </Button>
            </div>
          </>
        );
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0D0B1A] via-[#0a0a0a] to-[#1a0a1a] p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-block px-4 py-2 bg-gradient-to-r from-yellow-500/20 to-orange-500/20 border border-yellow-500/30 rounded-full mb-4">
            <span className="text-yellow-400 font-semibold">Guardian Dragon Device</span>
          </div>
          <h1 className="text-5xl font-bold bg-gradient-to-r from-yellow-400 via-orange-500 to-red-500 bg-clip-text text-transparent mb-2">
            Nari Device Connector
          </h1>
          <p className="text-slate-400 text-lg">
            Your personal guardian dragon for crypto portfolio protection
          </p>
        </div>

        {/* Main Card */}
        <Card className="nari-device-card bg-slate-900/50 border-slate-800 backdrop-blur-xl p-8 md:p-12 shadow-2xl">
          <div className="nari-avatar">
            {renderContent()}
          </div>
        </Card>

        {/* Info Section */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card className="bg-slate-900/30 border-slate-800 p-6">
            <h3 className="text-xl font-semibold text-yellow-400 mb-3">What is Nari?</h3>
            <p className="text-slate-400">
              Nari is a physical IoT device shaped like a dragon that connects to your Nardium wallet. 
              It provides real-time market alerts through visual and audio notifications, keeping you 
              informed about your portfolio 24/7.
            </p>
          </Card>
          <Card className="bg-slate-900/30 border-slate-800 p-6">
            <h3 className="text-xl font-semibold text-orange-400 mb-3">Features</h3>
            <ul className="text-slate-400 space-y-2">
              <li className="flex items-start gap-2">
                <span className="text-yellow-500 mt-1">•</span>
                <span>Real-time price alerts with LED notifications</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-yellow-500 mt-1">•</span>
                <span>Wallet activity monitoring and security alerts</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-yellow-500 mt-1">•</span>
                <span>Wi-Fi connected with blockchain sync</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-yellow-500 mt-1">•</span>
                <span>Customizable alert thresholds</span>
              </li>
            </ul>
          </Card>
        </div>
      </div>
    </div>
  );
}
